import { log } from './debug';

const observers = new Map();
const elementWatchers = new WeakMap();

/**
 * Attach event listener to an element with deduplication
 * @param {Element} element - The element to attach event to
 * @param {string} event - Event type
 * @param {Function} callback - Callback function
 * @param {boolean} once - Execute only once
 * @param {string} identifier - Unique identifier for this watcher
 */
function attachEventToElement(element, event, callback, once, identifier) {
  if (!element?.addEventListener) {
    return;
  }

  const eventKey = `${identifier}-${event}`;

  if (!elementWatchers.has(element)) {
    elementWatchers.set(element, new Map());
  }

  const elementEvents = elementWatchers.get(element);

  if (elementEvents.has(eventKey)) {
    return;
  }

  log.debug('DynamicContentObserver: Attaching event to element:', element, event);

  const eventHandler = (e) => {
    if (once) {
      element.removeEventListener(event, eventHandler, true);

      if (elementWatchers.has(element)) {
        const events = elementWatchers.get(element);
        events.delete(eventKey);
      }
    }

    callback(e, element);
  };

  element.addEventListener(event, eventHandler, true);
  elementEvents.set(eventKey, { eventHandler, event });
}

/**
 * Watch for elements matching a selector and execute callback when found
 * @param {string} selector - CSS selector to watch for
 * @param {Function} callback - Callback function to execute when element is found
 * @param {Object} options - Configuration options
 * @param {string} options.event - Event type to listen for (default: 'click')
 * @param {boolean} options.once - Execute callback only once per element (default: false)
 * @param {boolean} options.immediate - Check for existing elements immediately (default: true)
 * @param {Element} options.root - Root element to observe (default: document.body)
 * @param {string} options.observerId - Unique identifier for this observer
 * @returns {string} Observer ID for cleanup
 */
function processExistingElements({ selector, event, callback, once }) {
  document.querySelectorAll(selector).forEach((el) => attachEventToElement(el, event, callback, once, selector));
}

function handleAddedNodes(nodes, { selector, event, callback, once }) {
  Array.from(nodes).forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    if (node.matches(selector)) {
      attachEventToElement(node, event, callback, once, selector);
    }

    if (node.querySelectorAll) {
      node.querySelectorAll(selector).forEach((el) => attachEventToElement(el, event, callback, once, selector));
    }
  });
}

function createMutationHandler(context) {
  return (mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === 'childList') {
        handleAddedNodes(mutation.addedNodes, context);
      }
    });
  };
}

export function watchForElements(selector, callback, options = {}) {
  const {
    event = 'click',
    once = false,
    immediate = true,
    root = document.body,
    observerId = `observer-${Date.now()}-${Math.random()}`
  } = options;

  log.debug('DynamicContentObserver: Starting to watch for selector:', selector);

  const context = { selector, event, callback, once };

  if (immediate) {
    processExistingElements(context);
  }

  const observer = new MutationObserver(createMutationHandler(context));
  observer.observe(root, { childList: true, subtree: true });

  observers.set(observerId, observer);
  return observerId;
}

/**
 * Stop observing with a specific observer ID
 * @param {string} observerId - Observer ID to stop
 */
export function stopObserving(observerId) {
  const observer = observers.get(observerId);
  if (observer) {
    observer.disconnect();
    observers.delete(observerId);
    log.debug('DynamicContentObserver: Stopped observing:', observerId);
  }
}

/**
 * Stop all observers and clean up
 * Useful for page navigation or cleanup
 */
export function stopAllObserving() {
  observers.forEach((observer, id) => {
    observer.disconnect();
    log.debug('DynamicContentObserver: Stopped observing:', id);
  });

  observers.clear();
}

export const dynamicContentObserver = {
  watchForElements,
  stopObserving,
  stopAllObserving
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    stopAllObserving();
  });
}
